import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import authNavigate from "../../../globalComponents/AuthNav";
import ErrorPopup from "../../../utils/popup/ErrorPopup";
import ProcessingPopup from "../../../utils/popup/ProcessingPopup";
import SuccessPopup from "../../../utils/popup/SuccessPopup";
import PaginationControls from "../../../utils/PaginationControls";
import DeleteConfirmationModal from "../../../utils/DeleteConfirmationModal";
import TrimContent from "../../../utils/TrimContent";
import { UserCircleDisplay } from "../../../utils/UserCustomDisplay";
import { useTimezone } from '../../../utils/TimezoneContext';

const SOURCE_TYPE_CONFIG = {
    PUBMED:          { label: "PubMed",         badgeClass: "ev-badge-blue"   },
    FDA:             { label: "FDA",             badgeClass: "ev-badge-orange" },
    CLINICAL_TRIAL: { label: "Clinical Trials", badgeClass: "ev-badge-purple" },
    EMA:             { label: "EMA",             badgeClass: "ev-badge-green" },
    // EMBASE:       { label: "Embase",          badgeClass: "ev-badge-teal"   },
    // WHO_ICTRP:    { label: "WHO ICTRP",       badgeClass: "ev-badge-red"    },
};

const SOURCE_TYPE_OPTIONS = [
    { id: "ALL", label: "All" },
    ...Object.entries(SOURCE_TYPE_CONFIG).map(([id, { label }]) => ({ id, label })),
];

const SourceTypeFilter = ({ value, onChange }) => {
    const [open, setOpen] = useState(false);
    const ref = React.useRef(null);

    React.useEffect(() => {
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const selected = SOURCE_TYPE_OPTIONS.find((o) => o.id === value) || SOURCE_TYPE_OPTIONS[0];

    return (
        <div className="position-relative" ref={ref}>
            <div
                className="d-flex align-items-center gap-1 cursor-pointer"
                onClick={() => setOpen((p) => !p)}
            >
                <label className="cursor-pointer">Source Type</label>
                <i className="bi bi-funnel-fill ev-fs-11"></i>
            </div>
            {open && (
                <div className="ev-card-8 position-absolute top-100 end-0 mt-1 p-2 ev-radius-xsmall d-flex flex-column" style={{ zIndex: 9999, minWidth: "160px" }}>
                    {SOURCE_TYPE_OPTIONS.map((opt) => (
                        <button
                            key={opt.id}
                            type="button"
                            className={`w-100 text-start ev-radius-xsmall ev-btn-small mb-1 ${
                                value === opt.id
                                    ? "ev-btn-blue-fill-round"
                                    : "ev-btn-light-fill ev-reg-p-blue"
                            }`}
                            onClick={() => {
                                onChange(opt.id);
                                setOpen(false);
                            }}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

const LiteratureSearchesList = ({
    searches = [],
    total = 0,
    page = 1,
    setPage,
    size = 25,
    setSize,
    search = "",
    setSearch,
    onRefresh,
    onOpenDashboard,
    onDeleteSearch,
}) => {
    const navigate = useNavigate();
    const [searchInput, setSearchInput] = useState(search);
    const [sourceTypeFilter, setSourceTypeFilter] = useState("ALL");
    const { formatDateTime } = useTimezone();
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [searchToDelete, setSearchToDelete] = useState(null);

    const [isProcessingPopupVisible, setIsProcessingPopupVisible] = useState(false);
    const [processingMessage, setProcessingMessage] = useState("");
    const [popupErrorMessage, setPopupErrorMessage] = useState("");
    const [popupSuccessMessage, setPopupSuccessMessage] = useState("");

    const handleSearchInputChange = (e) => setSearchInput(e.target.value);

    const handleSearch = () => {
        if (setSearch) setSearch(searchInput);
        if (setPage) setPage(1);
    };

    const handleSearchKeyPress = (e) => {
        if (e.key === "Enter") handleSearch();
    };

    const confirmDelete = (row) => {
        setSearchToDelete(row);
        setShowDeleteConfirm(true);
    };

    const closeDeleteModal = () => {
        setShowDeleteConfirm(false);
        setSearchToDelete(null);
    };

    const handleDelete = async () => {
        setShowDeleteConfirm(false);
        if (!searchToDelete) return;

        if (!onDeleteSearch) {
            console.error("onDeleteSearch prop is missing");
            return;
        }

        try {
            setIsProcessingPopupVisible(true);
            setProcessingMessage("Deleting search...");

            await onDeleteSearch(searchToDelete);

            setPopupSuccessMessage("Search deleted successfully");
        } catch (e) {
            if (e.response?.data?.status === "NotAuthorized") {
                authNavigate('/login', 'iam', navigate);
                return;
            }

            const errorMessage = e.response?.data?.message
                || e?.message
                || "Failed to delete search";

            setPopupErrorMessage(errorMessage);
        } finally {
            setIsProcessingPopupVisible(false);
            setProcessingMessage("");
            setSearchToDelete(null);
        }
    };

    const totalPages = Math.ceil((total || 0) / (size || 25)) || 1;

    const normalizeAccessUsers = (row) => {
        const users = row.discovery_access || row.users || [];

        if (!Array.isArray(users)) return [];

        return users
            .map((x) => {
                if (!x) return null;
                if (typeof x === "string") return { email: x };
                if (x.email) return { email: x.email };
                return null;
            })
            .filter(Boolean);
    };

    const getDeleteModalBodyContent = () => {
        if (!searchToDelete) return null;
        return (
            <div>
                <p className="mb-0">This will permanently delete this literature search and its saved metadata.</p>
                <p className="text-danger mb-0">This action cannot be undone.</p>
            </div>
        );
    };

    return (
        <div className="ev-card-4 w-100 ev-padding-large">
            <div className="row mt-1 mb-3 align-items-center">
                <div className="col-6 position-relative">
                    <input
                        type="text"
                        className="form-control search-input ms-0"
                        placeholder="Search"
                        value={searchInput}
                        onChange={handleSearchInputChange}
                        onKeyPress={handleSearchKeyPress}
                    />
                    <button
                        type="button"
                        className="ev-btn-lightblue-fill-round search-bar-button"
                        onClick={handleSearch}
                        title="Search"
                    >
                        <i className="bi bi-search"></i>
                    </button>
                </div>

                <div className="col-6 d-flex justify-content-end align-items-center gap-2">
                    <button
                        className="ev-btn-lightblue-fill-round ev-bold-reg"
                        onClick={onRefresh}
                        type="button"
                    >
                        <i className="bi bi-arrow-repeat"></i> Refresh
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="table-container">
                <table className="table-custom1 mt-0 w-100">
                    <thead>
                        <tr>
                            <th className="w-20"><label>Name</label></th>
                            <th className="w-10"><label>Owner</label></th>
                            <th className="w-10 text-nowrap ps-5">
                                <SourceTypeFilter
                                    value={sourceTypeFilter}
                                    onChange={(val) => {
                                        setSourceTypeFilter(val);
                                        if (setPage) setPage(1);
                                    }}
                                />
                            </th>
                            <th className="w-12 ps-3"><label>Discovery Access</label></th>
                            <th className="w-13 ps-4"><label>Created At</label></th>
                            <th className="w-13"><label>Last Accessed At</label></th>
                            <th className="w-7 text-center"><label>Records</label></th>
                            <th className="w-15 text-center"><label>User Actions</label></th>
                        </tr>
                    </thead>
                </table>

                <div className="scrollable-element dashboard-right-contentarea-table-height-1">
                    <table className="table-custom1 overflow-hidden w-100">
                        <tbody>
                            {searches.filter((row) =>
                                sourceTypeFilter === "ALL" ||
                                (row.source_type || "PUBMED") === sourceTypeFilter
                            ).length === 0 ? (
                                <tr>
                                    <td colSpan="8" className="text-center py-5">
                                        <p className="ev-small-p-blue mb-0">No literature searches found</p>
                                    </td>
                                </tr>
                            ) : (
                                searches
                                    .filter((row) =>
                                        sourceTypeFilter === "ALL" ||
                                        (row.source_type || "PUBMED") === sourceTypeFilter
                                    )
                                    .map((row) => (
                                    <tr key={row.id}>
                                        <td className="align-top w-20">
                                            <div className="ev-small-p-dark mb-0 avatar-tooltip mb-1" data-tooltip={row.name || row.search_query}>
                                                &nbsp;<TrimContent text={row.name || row.search_query || ""} maxLength={35} />
                                            </div>
                                        </td>

                                        <td className="align-top w-10 avatar-tooltip" data-tooltip={row.owner}>
                                            <div className="ev-message-card5 ev-fs-11">
                                                <TrimContent text={row.owner || ""} maxLength={22} />
                                            </div>
                                        </td>

                                        <td className="align-top w-10">
                                            <span className={`ev-badge-pill ev-fs-11 ${SOURCE_TYPE_CONFIG[row.source_type]?.badgeClass || "ev-badge-blue"}`}>
                                                {SOURCE_TYPE_CONFIG[row.source_type]?.label || row.source_type || "PUBMED"}
                                            </span>
                                        </td>

                                        <td className="align-top w-15">
                                            {normalizeAccessUsers(row).length > 0 ? (
                                                <UserCircleDisplay
                                                    users={normalizeAccessUsers(row)}
                                                    maxDisplay={4}
                                                />
                                            ) : (
                                                <span className="ev-small-p-dark">None</span>
                                            )}
                                        </td>

                                        <td className="align-top w-15">
                                            {row.created_at ? formatDateTime(row.created_at) : "—"}
                                        </td>
                                        <td className="align-top w-15">
                                            {row.updated_at ? formatDateTime(row.updated_at) : "—"}
                                        </td>

                                        <td className="align-top w-5">
                                            <span className="ev-reg-p-blue ev-bold-med">{row.number_of_citations ?? 0}</span>
                                        </td>

                                        <td className="align-top w-15">
                                            <div className="d-flex justify-content-end align-items-center gap-2">
                                                <button
                                                    className="ev-btn-blue-outline-round ev-btn-small text-nowrap"
                                                    type="button"
                                                    onClick={() => onOpenDashboard && onOpenDashboard(row)}
                                                >
                                                    Discovery Dashboard
                                                </button>
                                                <button
                                                    className="btn ev-btn-red-fill-round ev-btn-med ev-radius-xsmall pe-2 ps-2 avatar-tooltip"
                                                    type="button"
                                                    onClick={() => confirmDelete(row)}
                                                    data-tooltip="Delete Search"
                                                >
                                                    <i className="bi bi-trash"></i>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="d-flex justify-content-between align-items-center mt-3">
                <div className="ev-small-p-dark">
                    Showing {searches.length} of {total} entries
                </div>
                <div className="d-flex gap-2 align-items-center">
                    <button
                        className="ev-btn-light-fill"
                        disabled={page === 1}
                        onClick={() => setPage && setPage(page - 1)}
                        type="button"
                    >
                        <i className="bi bi-chevron-left"></i>
                    </button>
                    <button className="ev-btn-light-fill" disabled type="button">
                        Page {page} of {totalPages}
                    </button>
                    <button
                        className="ev-btn-light-fill"
                        disabled={page >= totalPages}
                        onClick={() => setPage && setPage(page + 1)}
                        type="button"
                    >
                        <i className="bi bi-chevron-right"></i>
                    </button>
                </div>
                <div>
                    <PaginationControls
                        initialSize={size}
                        onSizeChange={(newSize) => {
                            if (setSize) setSize(newSize);
                            if (setPage) setPage(1);
                        }}
                    />
                </div>
            </div>

            {isProcessingPopupVisible && (
                <ProcessingPopup message={processingMessage || "Working..."} />
            )}

            {popupErrorMessage && (
                <ErrorPopup
                    onClose={() => setPopupErrorMessage("")}
                    message={popupErrorMessage}
                />
            )}

            {popupSuccessMessage && (
                <SuccessPopup
                    onClose={() => setPopupSuccessMessage("")}
                    message={popupSuccessMessage}
                />
            )}

            <DeleteConfirmationModal
                show={showDeleteConfirm}
                onClose={closeDeleteModal}
                onConfirm={handleDelete}
                objectName="Literature search"
                itemName={(searchToDelete?.name || searchToDelete?.search_query) ? String(searchToDelete.name || searchToDelete.search_query).slice(0, 40) : ""}
                bodyContent={getDeleteModalBodyContent()}
            />
        </div>
    );
};

export default LiteratureSearchesList;